import React, { useEffect, useState } from 'react'
import { TEXTES_BETA } from '@semply/theme/beta'

/**
 * Back-office › Bêta : période, affichage, textes et blocages (15/10/2026).
 *
 * Les textes se règlent à deux niveaux : COMMUNS à la suite, puis remplacés
 * produit par produit si besoin. Un champ vide reprend le niveau au-dessus,
 * puis le texte par défaut (@semply/theme/beta), affiché en indication.
 */

/** « 2026-11-02T09:00 » (heure locale, champ datetime-local) ↔ ISO. */
export function versChampDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const decale = new Date(d.getTime() - d.getTimezoneOffset() * 60_000)
  return decale.toISOString().slice(0, 16)
}
export const depuisChampDate = (valeur) => (valeur ? new Date(valeur).toISOString() : null)

const TEXTE_MODALE_DEFAUT = TEXTES_BETA.avertissement.join('\n')

/** Champs de texte, partagés entre le réglage commun et celui d'un produit. */
function ChampsTextes({ valeurs, changer, indications, disabled }) {
  return (
    <div className="admin-beta-textes">
      <label>
        <span>Texte du bandeau</span>
        <textarea
          rows={2}
          maxLength={500}
          value={valeurs.texteBandeau}
          placeholder={indications.texteBandeau}
          disabled={disabled}
          onChange={(e) => changer({ texteBandeau: e.target.value })}
        />
      </label>
      <label>
        <span>Titre de la fenêtre de connexion</span>
        <input
          maxLength={200}
          value={valeurs.titreModale}
          placeholder={indications.titreModale}
          disabled={disabled}
          onChange={(e) => changer({ titreModale: e.target.value })}
        />
      </label>
      <label>
        <span>Texte de la fenêtre (un point par ligne)</span>
        <textarea
          rows={5}
          maxLength={4000}
          value={valeurs.texteModale}
          placeholder={indications.texteModale}
          disabled={disabled}
          onChange={(e) => changer({ texteModale: e.target.value })}
        />
      </label>
    </div>
  )
}

const textesDe = (t) => ({
  texteBandeau: t.texteBandeau ?? '',
  titreModale: t.titreModale ?? '',
  texteModale: t.texteModale ?? '',
})

const INDICATIONS_DEFAUT = {
  texteBandeau: TEXTES_BETA.bandeau,
  titreModale: TEXTES_BETA.titreAvertissement,
  texteModale: TEXTE_MODALE_DEFAUT,
}

/** Textes communs à toute la suite. */
export function TextesCommunsBeta({ textes, enregistrer, writable, busy }) {
  const [valeurs, setValeurs] = useState(textesDe(textes))
  useEffect(() => setValeurs(textesDe(textes)), [textes])
  return (
    <section className="admin-platform-card">
      <h2>Textes communs</h2>
      <p className="admin-platform-hint">
        Affichés par tous les produits en bêta, sauf ceux qui ont leurs propres textes. Un champ vide
        reprend le texte par défaut (en gris).
      </p>
      <ChampsTextes
        valeurs={valeurs}
        changer={(v) => setValeurs({ ...valeurs, ...v })}
        indications={INDICATIONS_DEFAUT}
        disabled={!writable || busy}
      />
      {writable && (
        <div className="admin-platform-actions admin-beta-actions">
          <button type="button" disabled={busy} onClick={() => enregistrer(valeurs)}>
            Enregistrer les textes communs
          </button>
        </div>
      )}
    </section>
  )
}

const reglagesDe = (p) => ({
  debut: versChampDate(p.debut),
  fin: versChampDate(p.fin),
  bandeauActif: p.bandeauActif,
  modaleActive: p.modaleActive,
  bloquerValidationFactures: p.bloquerValidationFactures,
  bloquerPlateformeAgreee: p.bloquerPlateformeAgreee,
  ...textesDe(p),
})

/** Corps de la requête `PUT /admin/beta/:produit/reglages`. */
export const corpsReglages = (v) => ({
  debut: depuisChampDate(v.debut),
  fin: depuisChampDate(v.fin),
  bandeauActif: v.bandeauActif,
  modaleActive: v.modaleActive,
  bloquerValidationFactures: v.bloquerValidationFactures,
  bloquerPlateformeAgreee: v.bloquerPlateformeAgreee,
  texteBandeau: v.texteBandeau || null,
  titreModale: v.titreModale || null,
  texteModale: v.texteModale || null,
})

/** Réglages d'un produit : période, affichage, textes propres, blocages (App). */
export function ReglagesProduitBeta({ produit, communs, enregistrer, writable, busy }) {
  const [v, setV] = useState(reglagesDe(produit))
  useEffect(() => setV(reglagesDe(produit)), [produit])
  const changer = (m) => setV({ ...v, ...m })
  const desactive = !writable || busy
  const indications = {
    texteBandeau: communs.texteBandeau || INDICATIONS_DEFAUT.texteBandeau,
    titreModale: communs.titreModale || INDICATIONS_DEFAUT.titreModale,
    texteModale: communs.texteModale || INDICATIONS_DEFAUT.texteModale,
  }
  return (
    <details className="admin-beta-reglages">
      <summary>Période, affichage, textes et blocages</summary>

      <h3 className="admin-beta-sous-titre">Période</h3>
      <p className="admin-platform-hint">
        Hors de la période, le produit est en production quel que soit le mode ci-dessus : une bêta
        en production d’une ou deux semaines s’arrête d’elle-même. Vide = sans limite.
      </p>
      <div className="admin-beta-periode">
        <label>
          <span>Début</span>
          <input type="datetime-local" value={v.debut} disabled={desactive} onChange={(e) => changer({ debut: e.target.value })} />
        </label>
        <label>
          <span>Fin</span>
          <input type="datetime-local" value={v.fin} disabled={desactive} onChange={(e) => changer({ fin: e.target.value })} />
        </label>
      </div>

      <h3 className="admin-beta-sous-titre">Affichage</h3>
      <div className="admin-beta-cases">
        <label>
          <input type="checkbox" checked={v.bandeauActif} disabled={desactive} onChange={(e) => changer({ bandeauActif: e.target.checked })} />
          Bandeau rouge en haut de chaque page
        </label>
        <label>
          <input type="checkbox" checked={v.modaleActive} disabled={desactive} onChange={(e) => changer({ modaleActive: e.target.checked })} />
          Fenêtre d’avertissement à chaque connexion
        </label>
      </div>

      <h3 className="admin-beta-sous-titre">Textes propres à ce produit</h3>
      <p className="admin-platform-hint">Vide = texte commun (en gris).</p>
      <ChampsTextes valeurs={v} changer={changer} indications={indications} disabled={desactive} />

      {produit.produit === 'APP' && (
        <>
          <h3 className="admin-beta-sous-titre">Blocages</h3>
          <div className="admin-beta-cases">
            <label>
              <input
                type="checkbox"
                checked={v.bloquerValidationFactures}
                disabled={desactive}
                onChange={(e) => changer({ bloquerValidationFactures: e.target.checked })}
              />
              Bloquer la validation des factures (elles restent en brouillon)
            </label>
            <label>
              <input
                type="checkbox"
                checked={v.bloquerPlateformeAgreee}
                disabled={desactive}
                onChange={(e) => changer({ bloquerPlateformeAgreee: e.target.checked })}
              />
              Bloquer la plateforme agréée (connexion, réglages, envois et e-reporting)
            </label>
          </div>
        </>
      )}

      {writable && (
        <div className="admin-platform-actions admin-beta-actions">
          <button type="button" disabled={busy} onClick={() => enregistrer(corpsReglages(v))}>
            Enregistrer ces réglages
          </button>
        </div>
      )}
    </details>
  )
}
